import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { CTABlock } from "@/components/CTABlock";
import { company } from "@/data/site";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy | McKay Roofing & Solar" },
      { name: "description", content: "How McKay Roofing & Solar collects, uses and protects your information across our website, SMS and email communications." },
      { property: "og:title", content: "Privacy Policy – McKay Roofing & Solar" },
      { property: "og:description", content: "Our privacy practices for website visitors, customers and SMS/email subscribers." },
      { property: "og:url", content: "https://mckay.steadyautogrowthdemo.com/privacy" },
    ],
    links: [{ rel: "canonical", href: "https://mckay.steadyautogrowthdemo.com/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  const updated = "January 14, 2026";
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        {/* Hero – background only */}
        <section className="relative w-full overflow-hidden bg-primary text-primary-foreground">
          <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary/85" />
          <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_20%_20%,hsl(var(--secondary)/0.35),transparent_45%),radial-gradient(circle_at_80%_60%,hsl(var(--primary-foreground)/0.15),transparent_50%)]" />
          <div className="relative mx-auto flex min-h-[50vh] w-full max-w-5xl items-center px-4 py-20 sm:px-6 lg:px-8">
            <div>
              <h1 className="text-5xl font-bold sm:text-6xl text-balance">Privacy Policy</h1>
              <p className="mt-4 text-primary-foreground/80">Last updated: {updated}</p>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:px-8 prose prose-slate max-w-none">
          <h2 className="mt-2 text-2xl font-bold">1. Introduction</h2>
          <p>
            McKay Roofing &amp; Solar ("we", "us", "our") is committed to protecting your privacy. This
            Privacy Policy explains how we collect, use and protect your information when you use our
            website and services, including SMS marketing, email marketing, appointment reminders and
            website tracking tools. By using our services or opting into our communications, you agree
            to this Privacy Policy and our Terms &amp; Conditions.
          </p>

          <h2 className="mt-8 text-2xl font-bold">2. Information we collect</h2>
          <p><strong>Contact information.</strong> We may collect your name, email address, phone number and mailing address when you submit forms, book appointments, request information or otherwise share it with us.</p>
          <p><strong>Communications data.</strong> If you opt in, we collect your messaging preferences and keep records of consent and opt-out activity related to SMS and email communications.</p>
          <p><strong>Usage and device information.</strong> We may use cookies, pixels and analytics tools to understand how our website is used. This can include IP address, browser type, pages visited and interaction data.</p>
          <p><strong>Appointment and service data.</strong> If you schedule appointments or use our services, we may collect details tied to those interactions.</p>

          <h2 className="mt-8 text-2xl font-bold">3. How we use your information</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Provide requested services and customer support</li>
            <li>Send appointment confirmations and reminders</li>
            <li>Send SMS and email communications you have explicitly consented to receive</li>
            <li>Improve our website and marketing performance using analytics</li>
            <li>Maintain compliance records related to consent and opt-outs</li>
            <li>Protect our business and users, including fraud and security monitoring</li>
          </ul>

          <h2 className="mt-8 text-2xl font-bold">4. Cookies and tracking technologies</h2>
          <p>
            We use cookies and similar technologies to personalize your experience and understand how
            our website is used. You can control cookies through your browser settings; some features
            may not work correctly if cookies are disabled.
          </p>

          <h2 className="mt-8 text-2xl font-bold">5. How we share information</h2>
          <p>
            We do not sell your personal information. We do not share mobile phone numbers, SMS opt-in
            data or consent information with third parties or affiliates for marketing or promotional
            purposes. We share information only in the following limited circumstances:
          </p>
          <p><strong>Service providers.</strong> We may share information with trusted vendors that help us deliver services, such as SMS and email delivery, customer support tools, scheduling tools and analytics providers. These providers may only use information to perform services on our behalf.</p>
          <p><strong>Legal and safety.</strong> We may disclose information if required by law or to protect the rights, safety and security of {company.name}, our users or others.</p>
          <p><strong>Business transfers.</strong> If we are involved in a merger, acquisition or asset sale, information may be transferred as part of that transaction. Any successor will be required to honor this Privacy Policy.</p>
          <p><strong>Third-party sharing.</strong> No mobile information will be shared with third parties or affiliates for marketing or promotional purposes. Sharing with subcontractors for support services (such as customer service) is permitted. All other use case categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.</p>

          <h2 className="mt-8 text-2xl font-bold">6. Your choices</h2>
          <p><strong>SMS opt-out.</strong> You can opt out of SMS messages at any time by replying STOP to any message. After you send STOP, we will send a confirmation and you will no longer receive SMS messages unless you opt back in.</p>
          <p><strong>Email opt-out.</strong> You can unsubscribe from marketing emails by clicking the unsubscribe link in the email.</p>
          <p><strong>Access and correction.</strong> You may contact us to request access to, correction of or deletion of your personal information, subject to applicable law.</p>

          <h2 className="mt-8 text-2xl font-bold">7. Data security</h2>
          <p>
            We use reasonable administrative, technical and physical safeguards to protect your
            information. No method of transmission or storage is fully secure, so we cannot guarantee
            absolute security.
          </p>

          <h2 className="mt-8 text-2xl font-bold">8. Children&apos;s privacy</h2>
          <p>Our services are not intended for children under 13, and we do not knowingly collect personal information from children.</p>

          <h2 className="mt-8 text-2xl font-bold">9. Updates to this policy</h2>
          <p>We may update this Privacy Policy from time to time. The updated version will be posted with a new "Last updated" date.</p>

          <h2 className="mt-8 text-2xl font-bold">10. Contact us</h2>
          <p>
            {company.name}<br />
            9520 Padgett St, Suite 107, San Diego, CA 92126<br />
            <a href={company.phoneHref} className="text-primary underline">{company.phone}</a><br />
            <a href={`mailto:${company.email}`} className="text-primary underline">{company.email}</a>
          </p>
        </section>
        <CTABlock title="Ready for a free roofing or solar quote?" subtitle="Tell us about your project. We usually respond same day." />
      </main>
      <SiteFooter />
    </div>
  );
}
